import { Component, type ReactNode, type ErrorInfo } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangleIcon } from "./Icons";
import { supabase } from "../lib/supabase";

interface Props  { children: ReactNode }
interface State  { hasError: boolean; error: Error | null; eventId: string | null }

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, eventId: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Send to Sentry and get back an event ID we can show the user
    const eventId = Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
    this.setState({ eventId });

    // Also write to system_errors so admins can see it in the monitor dashboard
    supabase.from("system_errors").insert({
      severity:   "error",
      error_type: "frontend_crash",
      message:    error.message,
      context: {
        sentry_event_id:  eventId ?? null,
        component_stack:  info.componentStack?.slice(0, 800) ?? null,
        url:              window.location.href,
        user_agent:       navigator.userAgent.slice(0, 200),
      },
    }).then();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: "100vh", background: "#0d1117",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: "DM Sans, sans-serif",
      }}>
        <div style={{
          maxWidth: 480, width: "100%",
          background: "#111827",
          border: "1px solid rgba(248,113,113,0.25)",
          borderRadius: 16, padding: "36px 32px", textAlign: "center",
        }}>
          {/* Icon */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AlertTriangleIcon size={26} color="#f87171" />
            </div>
          </div>

          <div style={{ fontSize: 20, fontWeight: 700, color: "#f9fafb", marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 8, lineHeight: 1.6 }}>
            An unexpected error occurred. Your data is safe — this is a display issue only.
          </div>

          {/* Error message */}
          {this.state.error && (
            <div style={{
              fontSize: 11, fontFamily: "DM Mono, monospace",
              color: "rgba(248,113,113,0.7)",
              background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.15)",
              borderRadius: 8, padding: "10px 14px", margin: "16px 0",
              textAlign: "left", wordBreak: "break-word",
            }}>
              {this.state.error.message}
            </div>
          )}

          {/* Sentry event ID — helps you find it in the dashboard */}
          {this.state.eventId && (
            <div style={{ fontSize: 10, fontFamily: "DM Mono, monospace", color: "rgba(255,255,255,0.2)", marginBottom: 16 }}>
              Error ID: {this.state.eventId}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
            <button
              onClick={() => this.setState({ hasError: false, error: null, eventId: null })}
              style={{ background: "linear-gradient(135deg,#06b6d4,#0891b2)", border: "none", borderRadius: 10, padding: "11px 24px", color: "#fff", fontFamily: "DM Sans, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "11px 24px", color: "#9ca3af", fontFamily: "DM Sans, sans-serif", fontSize: 14, cursor: "pointer" }}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
